/**
 * The Builder page-body view, extracted from routes/$.tsx (COM-188).
 *
 * Two reasons this lives in its own module rather than inside the catch-all:
 *
 *  1. **The homepage route must not inherit the catch-all's PDP/PLP weld.**
 *     `routes/$.tsx`'s default export statically imports `ProductPageContent`
 *     and `CategoryPageContent` for its `pdp` / `plp` payload kinds, which
 *     drags product-details, product-buy-form, search and both carousel
 *     libraries into any route chunk that imports it. `routes/index.tsx`
 *     (the homepage) can only ever receive a `builder` payload — its dispatch
 *     is Builder-override or a thrown SFRA proxy — so it imports this module
 *     and skips that graph entirely.
 *
 *  2. **The full block registry is loaded lazily on the client**
 *     (`~/hec/builder/registry-loader`). On a document load the client never
 *     renders `<Content>` until the page-tree fetch lands — CrashSafeSsrIsland
 *     paints the SSR snapshot — so the registry's ~360KB graph does not need
 *     to be fetched/parsed/evaluated during hydration at all. The server
 *     keeps synchronous access via `getLoadedCustomComponents()` (the loader
 *     awaits `loadCustomComponents()` before returning a builder payload).
 *
 * Hydration-parity note: none of this changes SSR markup. The island's client
 * hydration path renders the DOM snapshot (`dangerouslySetInnerHTML`) exactly
 * as before; the only behavioural difference is that the upgrade render now
 * additionally waits for the registry chunk (which loads in parallel with the
 * page-tree fetch it already waited on).
 */

/* eslint-disable react-refresh/only-export-components -- route-module-shaped
   shared view: exports the meta fragment + hasBlocks alongside components,
   same shape as routes/$.tsx (which exports headers/meta). */
import { startTransition, useEffect, useMemo, useState } from "react";
import { preload } from "react-dom";

import type { CIOBrowseProduct } from "~/hec/.server/cio-products.server";

import {
  type BuilderContent,
  Content,
  type RegisteredComponent,
} from "@builder.io/sdk-react";

import { GuardedBuilderLink } from "~/hec/builder/guarded-builder-link";
import { ModelMap } from "~/hec/builder/model-map";
import {
  getLoadedCustomComponents,
  loadCustomComponents,
} from "~/hec/builder/registry-loader";
import { BuilderPageFlashBanner } from "~/hec/components/builder-page-flash-banner";
import { builderPageIslandKey } from "~/hec/components/builder-page-island-key";
import { CrashSafeSsrIsland } from "~/hec/components/crash-safe-ssr-island";
import { PrefetchBuilderAnchors } from "~/hec/components/prefetch-builder-anchors";
import { CarouselProductsProvider } from "~/hec/context/carousel-products-context";
import { useBuilder } from "~/hec/hooks/useBuilder";
import { useBuilderPreviewContent } from "~/hec/hooks/useBuilderPreviewContent";
import { collectHeroImagePreloads } from "~/hec/utils/builder-image-priority";
import { writePageTreeReloadSignal } from "~/hec/utils/builder-page-tree-reload-signal";
import { peekSsrBuilderPage } from "~/hec/utils/ssr-builder-page";
import { safeSessionStorage } from "~/lib/utils/safe-storage";
import { useRootLayoutData } from "~/root";
import { reportClientError } from "~/utils/report-client-error";
import { maybeReloadOnVersionSkew } from "~/utils/version-skew-reload";

export type BuilderPagePayload = {
  path: string;
  page: { data?: { title?: string; description?: string } } | null;
  references: unknown;
  unresolvedCount: number;
  hideLayoutHeader: boolean;
  hideLayoutFooter: boolean;
  cioProducts: CIOBrowseProduct[];
  carouselCategoryId: string | null;
  /** CSP nonce used to stash the full tree for SSR; not the page itself. */
  ssrKey?: string;
  /**
   * Triage C8: the SERVING build's RR7 asset-manifest version. Optional on
   * the wire — a payload from a server older than this field simply carries
   * none and the skew check no-ops. See ~/utils/version-skew-reload.
   */
  buildVersion?: string;
};

/**
 * The `meta` fragment for a builder payload — shared by the catch-all's meta
 * (its `type === "builder"` branch) and the homepage route's meta, so the two
 * cannot drift.
 */
export function builderEntryMeta(
  builderPage: BuilderPagePayload["page"],
): Array<{ title?: string; name?: string; content?: string }> {
  return [
    { title: builderPage?.data?.title || "Page" },
    {
      name: "description",
      content: builderPage?.data?.description || "Page",
    },
  ];
}

export function hasBlocks(page: unknown): boolean {
  const blocks = (page as { data?: { blocks?: unknown[] } } | null)?.data
    ?.blocks;
  return Array.isArray(blocks) && blocks.length > 0;
}

function asBuilderContent(page: unknown): BuilderContent | undefined {
  return page && typeof page === "object"
    ? (page as BuilderContent)
    : undefined;
}

/**
 * The full page-block registry, resolved lazily on the client (COM-188).
 *
 * Server: non-null on first render — the catch-all loader awaits
 * `loadCustomComponents()` before returning a builder payload.
 *
 * Client: null until the deferred registry chunk lands, then set inside a
 * transition so the (large) re-render it unlocks is time-sliced instead of a
 * single long task. While null, `BuilderBody` keeps rendering the island's
 * SSR snapshot — the same thing it renders while the page-tree fetch is in
 * flight — so nothing is visually different during the wait.
 */
function useCustomComponentsRegistry(): RegisteredComponent[] | null {
  const [registry, setRegistry] = useState<RegisteredComponent[] | null>(
    getLoadedCustomComponents,
  );
  useEffect(() => {
    if (registry) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    // PR #1042 blocker 2: a rejected registry import used to be silently
    // swallowed here AND cached forever by the loader — a single transient
    // chunk failure left every Builder page body blank for the session with
    // zero observability. The loader now clears its in-flight cache on
    // rejection; this side retries once (a blip usually clears in seconds)
    // and beacons the terminal failure so "Builder pages went blank on nav"
    // reports are attributable. The SSR snapshot keeps rendering throughout,
    // and any later remount/navigation retries again via the cleared cache.
    const attempt = (retriesLeft: number) => {
      loadCustomComponents().then(
        loadedRegistry => {
          if (cancelled) return;
          startTransition(() => setRegistry(loadedRegistry));
        },
        (err: unknown) => {
          if (cancelled) return;
          if (retriesLeft > 0) {
            retryTimer = setTimeout(() => attempt(retriesLeft - 1), 2_000);
            return;
          }
          reportClientError("builder-registry-load", err, {
            path: window.location.pathname,
          });
        },
      );
    };
    attempt(1);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [registry]);
  return registry;
}

function BuilderBody({
  path,
  initialPage,
  apiKey,
  ssrKey,
}: {
  path: string;
  initialPage: unknown;
  apiKey: string;
  ssrKey?: string;
}) {
  const previewPage = useBuilderPreviewContent({
    initialContent: initialPage,
    model: ModelMap.pageModel,
  });
  const ssrPage =
    typeof window === "undefined" ? peekSsrBuilderPage(ssrKey) : null;
  // COM-188: null on the client until the deferred registry chunk resolves.
  // Non-null during SSR (loader contract — see useCustomComponentsRegistry).
  const customComponents = useCustomComponentsRegistry();
  // Shopper state for authored bindings (`state.smartSpenderEnrolled`, COM-468).
  const { smartSpenderEnrolled } = useRootLayoutData();
  // Memoized: `Content` runs an effect on `props.data` identity that re-renders
  // the whole Builder tree, so a fresh object per render would cost every page.
  const builderData = useMemo(
    () => ({ smartSpenderEnrolled }),
    [smartSpenderEnrolled],
  );
  // TAGGED WITH THE PATH IT BELONGS TO — do not store a bare page here.
  //
  // The `$` route renders this same component for every Builder page, so React
  // keeps the instance (and its state) across client navigations. When this was
  // a bare `useState<unknown>(null)`, clicking a homepage category link left
  // `fetched` holding the HOMEPAGE's tree, which then:
  //
  //   1. short-circuited the effect below via its `|| fetched` guard, so the
  //      new page's tree was never requested at all, and
  //   2. kept `liveContent` non-null, so `ready` stayed true and the island
  //      rendered the PREVIOUS page's <Content>.
  //
  // Net effect: the URL and <title> updated while the body kept showing the old
  // page until a hard refresh. Reproduced on develop — home ->
  // /patiently/fsa-weight-loss.html changed the title and left the island
  // byte-identical at 869,320 chars.
  const [fetched, setFetched] = useState<{
    path: string;
    page: unknown;
  } | null>(null);

  // Anything fetched for a different path is stale the moment `path` changes —
  // evaluated during render, so a navigation never gets one frame of the
  // previous page's content.
  const fetchedPage = fetched?.path === path ? fetched.page : null;

  // COM-189: preload the flagged hero. `eager` + `fetchpriority=high` only
  // says "do not defer me once found" — the hero `<img>` sits deep in the
  // streamed body, so discovery must come from a head preload.
  //
  // Wave 3: emitted via ReactDOM.preload(), NOT a rendered <link>. A rendered
  // <link rel="preload"> is a plain hoistable — React 19 Fizz flushes it
  // AFTER the coalesced `data-precedence` styles, which placed it at byte
  // ~273,000 of the document, behind ~272 KB of inline CSS (the COM-188 style
  // collapse moved every block style into <head> ahead of it). Measured on
  // the wave3-base preview (6 runs): LCP Load Delay — hero discovery — was
  // the dominant phase at a median 4,009 ms. preload() with fetchPriority
  // "high" enters Fizz's highImagePreloads queue, which flushes BEFORE the
  // precedence styles — first ~500 bytes of <head>. Note React omits `href`
  // on the emitted link when `imageSrcSet` is present; the srcset string
  // stays byte-identical to the SDK's, which is the double-download guard.
  //
  // COM-693: the list also carries the banner when it is painted as a CSS
  // `background-image` — one entry per breakpoint, `href` only (no srcset),
  // scoped by `media`. Those sit in `@media` rules of the inline Builder
  // <style>, which the preload scanner never reads, so without this the
  // mobile LCP image was first requested at DOMContentLoaded.
  //
  // Computed from the page being SSR'd (a preload that first appears on a
  // client re-render is pointless — the image is already requested by then).
  const heroPreloads = collectHeroImagePreloads(
    hasBlocks(previewPage) ? previewPage : ssrPage,
  );
  for (const hero of heroPreloads) {
    preload(hero.href, {
      as: "image",
      fetchPriority: "high",
      // No `imageSizes`, on purpose: the candidates are the SDK's webp
      // <source srcSet>, and that <source> carries no `sizes` (COM-693).
      imageSrcSet: hero.imageSrcSet,
      // Scopes each half of the display-toggled hero pair to the breakpoint
      // where it is visible. Without it, mobile preloads the desktop half at
      // high priority — measured 0x0 rendered, and it downloads anyway.
      media: hero.media,
    });
  }

  // Client navigations have no SSR snapshot (slim flight has no blocks).
  // Fetch the tree immediately so main is not empty until idle.
  useEffect(() => {
    // Guard on the page fetched FOR THIS PATH, not on "have we ever fetched".
    // `fetched` alone made this a no-op after the first navigation.
    if (hasBlocks(previewPage) || fetchedPage) return;
    // Client-side fetch, but the SFCC-10620 rule covers app/routes/** — an
    // AbortController satisfies it and also cancels the request on unmount.
    const controller = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    // Triage C7 (docs/release-15-review-triage-2026-09-09.md §3.1): this fetch
    // used to be single-shot with every failure swallowed — non-2xx → null and
    // rejections `.catch(() => undefined)` — so one transient failure (the
    // realistic production trigger is a bot-score WAF challenge on the
    // `/resources/*` XHR, which never reaches the Worker and is invisible on
    // WAF-less devhec) left the island's `min-h-[70vh]` fallback up until a
    // hard refresh, with zero telemetry. Mirror of the COM-352
    // `builder-registry-load` pattern above: one retry after ~2s on BOTH
    // non-OK responses and rejections, then a terminal beacon under a
    // DISTINCT scope so the two blank-body causes stay separable in
    // Observability, then a once-per-path reload as recovery — a document
    // load renders the tree server-side (SSR snapshot), so it does not depend
    // on this challengeable client XHR at all.
    const attempt = (retriesLeft: number) => {
      const fail = (err: unknown, status: number | null) => {
        // Unmount/navigation abort is cancellation, not failure.
        if (controller.signal.aborted) return;
        if (retriesLeft > 0) {
          retryTimer = setTimeout(() => attempt(retriesLeft - 1), 2_000);
          return;
        }
        reportClientError("builder-page-tree-fetch", err, { path, status });
        // Recovery: reload once per path per tab session. The guard key
        // embeds the path so one persistently-broken path cannot put the
        // session into a reload loop; a second terminal failure on the same
        // path keeps the current fallback (SSR snapshot or empty island)
        // instead of reloading again. The read-back check matters: with
        // storage blocked (SFCC-11876) `setItem` is a no-op, "once" would be
        // unenforceable, so skip the reload entirely rather than risk a loop.
        const guardKey = `hec_builder_page_tree_reloaded:${path}`;
        if (safeSessionStorage.getItem(guardKey)) return;
        safeSessionStorage.setItem(guardKey, "1");
        if (safeSessionStorage.getItem(guardKey) !== "1") return;
        // Second telemetry channel (PR #1120 review round 1). The beacon above
        // POSTs to /resources/client-error — the same WAF path class as the
        // fetch that just failed, so for the challenged cohort (non-US bot
        // score 1-29) it is challenged identically and never lands. A
        // document navigation gets a SOLVABLE interstitial, so plant a
        // 60s first-party cookie the reload's document request carries;
        // PageTreeReloadSignalMiddleware logs it once and clears it in the
        // same response (a lingering cookie would keep the shopper out of the
        // cookieless guest HTML cache). Only on the reload branch: without a
        // reload there is no document request to carry it.
        writePageTreeReloadSignal({ path, status, ts: Date.now() });
        window.location.reload();
      };
      void fetch(
        `/resources/builder-page-tree?path=${encodeURIComponent(path)}`,
        {
          signal: controller.signal,
        },
      )
        .then(res => {
          if (!res.ok) {
            fail(
              new Error(`builder-page-tree responded ${res.status}`),
              res.status,
            );
            return;
          }
          return res.json().then((json: unknown) => {
            const page =
              json && typeof json === "object" && "page" in json
                ? (json as { page?: unknown }).page
                : undefined;
            // Store the path alongside the tree. Without it a response that
            // lands after the shopper has already navigated on would be
            // adopted by the wrong page.
            //
            // COM-188: set inside a transition — this state flip unlocks the
            // island's upgrade render (the full Builder <Content> tree,
            // thousands of elements). At default priority that render was a
            // single long main-thread task inside the TBT window; a
            // transition lets React time-slice it. The commit (DOM swap)
            // stays atomic either way.
            if (!controller.signal.aborted && page) {
              startTransition(() => setFetched({ path, page }));
            }
          });
        })
        .catch((err: unknown) => fail(err, null));
    };
    attempt(1);
    return () => {
      controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [path, previewPage, fetchedPage]);

  // Never hand `<Content>` a slim id-only flight payload — the SDK treats
  // `{ id }` as enough to render the full page and we paint the homepage
  // twice (two eager copies of the same LCP image).
  const ssrContent = hasBlocks(ssrPage) ? asBuilderContent(ssrPage) : undefined;
  // `fetchedPage`, not `fetched` — a tree fetched for a different path must not
  // become this page's content.
  const liveContent = hasBlocks(fetchedPage)
    ? asBuilderContent(fetchedPage)
    : undefined;
  // COM-188: the registry gate applies to every client `<Content>` render.
  // Without it, `ready` could flip with a null upgrade and blank the island.
  const ready = hasBlocks(previewPage) || liveContent != null;

  if (hasBlocks(previewPage)) {
    // Editor/preview only (`useBuilderPreviewContent` overlays editor state).
    // On the client this subtree renders as soon as the registry chunk lands
    // (the hero preload is a render side-effect, not an element). A brief
    // blank here is editor-only and self-heals — live traffic never takes
    // this branch.
    return customComponents ? (
      <PrefetchBuilderAnchors>
        <Content
          model={ModelMap.pageModel}
          content={asBuilderContent(previewPage)}
          apiKey={apiKey}
          customComponents={customComponents}
          linkComponent={GuardedBuilderLink}
          data={builderData}
        />
      </PrefetchBuilderAnchors>
    ) : null;
  }

  return (
    <>
      <CrashSafeSsrIsland
        // Keyed on PATH, not a constant. Same value on server and client for a
        // given page (so the snapshot resolves and the body never blanks), but
        // different per page — a shared key let an incoming page snapshot the
        // OUTGOING page's still-mounted island, so a client navigation changed
        // the URL and <title> while the body kept showing the old page.
        islandKey={builderPageIslandKey(path)}
        ready={ready && customComponents != null}
        upgrade={
          liveContent && customComponents ? (
            <PrefetchBuilderAnchors>
              <Content
                model={ModelMap.pageModel}
                content={liveContent}
                apiKey={apiKey}
                customComponents={customComponents}
                linkComponent={GuardedBuilderLink}
                data={builderData}
              />
            </PrefetchBuilderAnchors>
          ) : null
        }
      >
        {ssrContent && customComponents ? (
          <PrefetchBuilderAnchors>
            <Content
              model={ModelMap.pageModel}
              content={ssrContent}
              apiKey={apiKey}
              customComponents={customComponents}
              linkComponent={GuardedBuilderLink}
              data={builderData}
            />
          </PrefetchBuilderAnchors>
        ) : null}
      </CrashSafeSsrIsland>
    </>
  );
}

export function BuilderPageView({ payload }: { payload: BuilderPagePayload }) {
  const {
    page: initialPage,
    cioProducts,
    carouselCategoryId,
    path,
    ssrKey,
  } = payload;
  const apiKey = useBuilder().config.api_key;
  const pageId =
    (initialPage as { id?: string } | null)?.id ??
    (peekSsrBuilderPage(ssrKey) as { id?: string } | null)?.id;

  // Triage C8: a soft navigation across a deploy delivers this payload from a
  // NEWER server whose flight shape this (stale) bundle may not understand —
  // release 15's slim block-less Builder flight is the motivating case. When
  // the payload's stamp differs from the loaded bundle's manifest version,
  // heal with one guarded document reload (beaconed under
  // "version-skew-reload"). A document load can never trip this — its flight
  // is rendered by the same build that served its scripts, so the stamp
  // matches by construction — and an absent stamp (older server) is a no-op.
  // Effect, not render: reading a field only, never a new response (the RR7
  // empty-204 fetcher trap), and never during SSR.
  const buildVersionStamp = payload.buildVersion;
  useEffect(() => {
    maybeReloadOnVersionSkew(buildVersionStamp);
  }, [buildVersionStamp]);

  return (
    // COM-187: `min-h-screen`, NOT `min-h-full`. This element reserves the
    // Builder page body's height, and the reservation is what keeps the shared
    // footer below the fold until the body has painted.
    //
    // `min-h-full` compiles to `min-height:100%`, which resolves against the
    // containing block's height. Nothing in the chain has a resolved height
    // (`app/styles/app.css` declares no `height` on `html` or `body`, and
    // `#maincontent` / `#blurrable-content` are auto), so the percentage
    // resolved to 0 and the reservation was a silent NO-OP. With zero
    // reserved height, `footer#footercontent` sat directly under the header at
    // y=734 — INSIDE the 823px mobile viewport — and was pushed to its settled
    // y=5256 the moment the body gained height. Chrome clips LayoutShift rects
    // to the viewport, so the footer's departure was reported as
    // `old=x=0 y=734 412x89  new=empty` and read as an unmount; it is a
    // push-down. Proof: score 0.1081409477521264 == 89/823 (visible slice /
    // viewport height) with a distance fraction of 1.0, and Chrome only scores
    // nodes that exist on BOTH sides of the shift and moved — an unmounted
    // node cannot score at all. Deterministic 0.108 on every homepage load.
    //
    // `min-h-screen` resolves through `--screen-height` (`100svh` where
    // supported, `100vh` otherwise) — both STATIC. Not `min-h-dvh`: a dynamic
    // unit re-resolves when the mobile URL bar collapses, which would author a
    // new shift of its own.
    <div className="min-h-screen">
      <CarouselProductsProvider
        value={{
          products: cioProducts ?? [],
          isLoading: false,
          categoryId: carouselCategoryId ?? undefined,
        }}
      >
        {/* The Builder body renders and hydrates DIRECTLY — no
            DeferredHydration gate (COM-186). Two incidents, one mechanism.
            SFCC-11887: the gate's retry-lane hydration of a dehydrated
            boundary raced the Builder `<Content>` thenable and threw React
            #482 during hydration, which escalates past nested error
            boundaries to RootErrorBoundary and painted the whole-site
            "services offline" screen. COM-186: with the flag forced on for a
            perf measurement, a provider state update landing inside the
            dehydrated window made React abandon the boundary, discard the
            server DOM and re-render this whole tree from scratch — ~550 ms of
            BLANK page body, dragging the footer through the viewport for
            CLS 0.303. Both are the same dehydrated-boundary abandonment,
            and it only shows up once the document is fast (edge-cached), so
            re-enabling this is now more dangerous than it used to be, not
            less. See app/components/deferred-hydration.tsx and
            docs/working-conventions.md. */}
        <BuilderBody
          path={path}
          initialPage={initialPage}
          apiKey={apiKey}
          ssrKey={ssrKey}
        />
      </CarouselProductsProvider>
      <BuilderPageFlashBanner pageId={pageId} />
    </div>
  );
}
